/* jshint esversion: 6 */
import React from 'react';
import * as constants from 'app/redux/constants.js';
import XQrCodesModal from '@/shadcn/components/XQrCodesModal.tsx';
import { assetBase } from '@/lib/asset-base.ts';
import makeToast from 'app/modules/makeToast.js';

/*
 * The X QR codes announcement: deciding who sees it, and turning QR codes on.
 *
 * App mounts this as the last of its login modals, so it only runs when no
 * other modal wants the slot. Whether an X account has QR codes on is only on
 * social/show (the list carries no params), so this asks for each X account in
 * turn, and decides once every answer is in. It shows if any has QR codes off.
 * Accounts that are already on are left out of the update.
 *
 * "Seen" is written to sessionStorage the moment that decision is made, either
 * way: an account with nothing to turn on is not asked again every page load,
 * and closing the modal is then just closing it.
 */

const SEEN_KEY = 'xQrCodesModalSeen';

// Accounts created on or after this date sign up after the change and never see it
const NEW_ACCOUNT_CUTOFF = '2026-09-28';

function xSocials(list, siteConstants) {
    return list.filter((social) => social.psID === siteConstants.POST_SVC_TWITTER);
}

function qrCodesOn(social) {
    return Number(social.params.postQrCode) === 1;
}

function markSeen() {
    try {
        window.sessionStorage.setItem(SEEN_KEY, '1');
    } catch (e) {
        // Storage refused: the worst case is seeing it again next page load
    }
}

export function xQrCodesNoticeSeen() {
    try {
        return window.sessionStorage.getItem(SEEN_KEY) !== null;
    } catch (e) {
        return false;
    }
}

/*
 * Whether App should mount the notice at all, from what is known without a
 * request. `created` is the server's "YYYY-MM-DD HH:MM:SS", so the date part
 * compares as a string.
 */
export function xQrCodesNoticeApplies(props) {
    let created = String(props.user.detail.created || '');

    return props.app.firstLoad.SOCIAL_LIST === true &&
        /^\d{4}-\d{2}-\d{2}/.test(created) &&
        created.slice(0, 10) < NEW_ACCOUNT_CUTOFF &&
        xSocials(props.socials.list, props.app.config.constants).length > 0;
}

export default function XQrCodesNotice(props) {
    /*
     * The registry callbacks are registered once, so they read the current
     * props through this rather than through the render they were made in.
     */
    const latest = React.useRef(props);
    latest.current = props;

    // The X accounts to turn on, fixed when the decision is made; null while checking
    const [targets, setTargets] = React.useState(null);
    const [busy, setBusy] = React.useState(false);
    const [error, setError] = React.useState(null);

    const requested = React.useRef(new Set());
    const decided = React.useRef(false);
    // The accounts still to update while a confirm is running, or null
    const queue = React.useRef(null);

    /*
     * One social/show at a time: app.loading has a single flag for every show in
     * flight, so it can only say when the last one settled if there is only
     * one. A show that failed leaves its account without params, and it is
     * skipped rather than asked for again.
     */
    const check = () => {
        if (decided.current) {
            return;
        }

        let { app, socials, getSocial, onDone } = latest.current,
            list = xSocials(socials.list, app.config.constants);

        if (app.loading[constants.SOCIAL_SHOW]) {
            return;
        }

        let next = list.find((social) => !social.params && !requested.current.has(social.id));

        if (next) {
            requested.current.add(next.id);
            getSocial(next.id);
            return;
        }

        decided.current = true;
        markSeen();

        let off = list.filter((social) => social.params && !qrCodesOn(social)).map((social) => social.id);

        if (off.length) {
            setTargets(off);
        } else {
            onDone();
        }
    };

    /*
     * social/update replaces the stored params with the defaults plus whatever
     * is sent, so each account is sent its full params from social/show with
     * postQrCode on. Anything less would reset its other X settings.
     */
    const updateNext = () => {
        let { socials, updateSocial, onDone } = latest.current;

        while (queue.current.length) {
            let id = queue.current.shift(),
                social = socials.list.find((candidate) => candidate.id === id);

            // Gone, or already on: after an earlier try, or turned on elsewhere
            if (!social || !social.params || qrCodesOn(social)) {
                continue;
            }

            updateSocial({
                id: social.id,
                params: Object.assign({}, social.params, { postQrCode: 1 })
            });
            return;
        }

        queue.current = null;
        makeToast('QR codes enabled for your X posts', 'success');
        onDone();
    };

    const updateSettled = () => {
        if (queue.current === null) {
            return;
        }

        if (latest.current.getApiErrors([constants.SOCIAL_UPDATE]).length) {
            // The accounts that did turn on are skipped when this is retried
            queue.current = null;
            setBusy(false);
            setError("We couldn't turn QR codes on for all of your X accounts. Please try again.");
            return;
        }

        updateNext();
    };

    React.useEffect(() => {
        let showSettled = { action: constants.SOCIAL_SHOW, func: check },
            updated = { action: constants.SOCIAL_UPDATE, func: updateSettled };

        props.registerCallback(showSettled);
        props.registerCallback(updated);

        check();

        return () => {
            props.unregisterCallback(showSettled);
            props.unregisterCallback(updated);
        };
    }, []);

    if (!targets) {
        return null;
    }

    let close = () => {
            // Once the updates are going out they finish; closing would strand the rest
            if (!busy) {
                props.onDone();
            }
        },
        linkPageHref = null;

    // The client app has no link page settings screen
    if (props.appName === 'dlvr') {
        linkPageHref = '/config/socials/detail/' + targets[0] + '/linkpage';
    }

    return (
        <XQrCodesModal
            assetBaseUrl={assetBase(props.app.config)}
            linkPageHref={linkPageHref}
            onLinkPageClick={() => {
                if (busy) {
                    return;
                }
                props.onDone();
                props.router.navigate(linkPageHref);
            }}
            busy={busy}
            error={error}
            onConfirm={() => {
                setError(null);
                setBusy(true);
                queue.current = targets.slice();
                updateNext();
            }}
            onClose={close}/>
    );
}
